import { useQuery, useQueryClient } from "react-query";

import getAccessToken from "./getAccessToken";

const { REACT_APP_API_URL } = process.env;

// Exportado: EHR.js#PrintExtras lo usa con queryClient.fetchQuery para poder
// esperar (await) un fetch puntual desde un click handler imperativo — algo
// que un useQuery declarativo no permite. Misma key/fetch que el resto del
// repo (ver Hook más abajo), para compartir cache con cualquier useQuery del
// mismo endpoint.
export const request = (key, accessToken, query, body, method, headers, timeout) => {
  const options = {
    headers: { ...headers },
  };
  if (accessToken) {
    options.headers.Authorization = `Bearer ${accessToken}`;
  }
  let qs = "";
  if (method) {
    options.method = method;
    if (method !== "GET" && method !== "HEAD") {
      options.headers["Content-Type"] = "application/json";
      if (body && body.constructor === Object) {
        options.body = JSON.stringify(body);
      }
    }
  } else if (body && body.constructor === Object) {
    options.method = "POST";
    options.headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }
  if (query && query.constructor === String) {
    qs = "?" + new URLSearchParams({ q: query }).toString();
  }
  
  // Si el endpoint es absoluto (http/https), no anteponer REACT_APP_API_URL
  const isAbsolute = /^https?:\/\//i.test(key);
  const pathWithQs = `${key}${qs}`;
  const url = isAbsolute
    ? pathWithQs
    : [REACT_APP_API_URL, pathWithQs.replace(/\/\/+/g, "/")].join("/");
  // Timeout opcional (ms): sin el, una request que no vuelve deja la query en
  // isLoading para siempre. No AbortSignal.timeout: jsdom y browsers viejos no lo tienen.
  let timer;
  if (timeout > 0 && typeof AbortController !== "undefined") {
    const controller = new AbortController();
    options.signal = controller.signal;
    timer = setTimeout(() => controller.abort(), timeout);
  }
  return fetch(url, options).then(async (res) => {
    const text = await res.text();
    
    if (!res.ok) {
      const error = new Error(`HTTP error! status: ${res.status}`);
      error.status = res.status;
      try {
        error.response = text ? JSON.parse(text) : null;
      } catch {
        error.response = text || null;
      }
      throw error;
    }
    
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

const Hook = (endpoint, options = { enabled: true }) => {
  const accessToken = getAccessToken();
  const queryClient = useQueryClient();

  if (options.enabled === undefined) {
    options.enabled = true;
  }

  // Bug real (auditoría 2026-08-16): refresh() reconstruía la key SIN
  // options.method, mientras que la query real de arriba SÍ la incluye —
  // para cualquier caller con options.method seteado, cancelQueries()
  // cancelaba la request real, pero setQueryData() escribía en una key de 3
  // elementos distinta a la de 4 elementos que el componente realmente
  // observa (hash exacto, no matching parcial): el refresh "pegaba" en una
  // entrada de caché fantasma que nadie lee, y la UI quedaba mostrando data
  // vieja disfrazada de refresh exitoso. Se arma la key una sola vez y se
  // reusa para la query y para refresh().
  //
  // headers NO forma parte de la key a propósito: hoy es constante por
  // endpoint. Si dos callers llegan a pegarle al mismo endpoint con headers
  // distintos, react-query les va a servir la misma cache sin distinguirlos.
  const queryKey = [endpoint, options.query, options.body, options.method].filter(Boolean);

  const query = useQuery(
    queryKey,
    () => request(endpoint, accessToken.value, options.query, options.body, options.method, options.headers, options.timeout),
    {
      ...options,
      refetchOnWindowFocus: false,
      enabled: !!(options.enabled && accessToken.value && endpoint),
      query: undefined,
      body: undefined,
      method: undefined,
      headers: undefined,
      timeout: undefined,
    }
  );

  query.refresh = data => {
    queryClient.cancelQueries(queryKey);
    queryClient.setQueryData(queryKey, JSON.clone(data));
  };

  return query;
};

export default Hook;
